import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { faSparkles, faStop } from '@awesome.me/kit-4a6bf60416/icons/classic/solid';
import { usePage } from '@inertiajs/react';
import { toast } from 'sonner';
import { v7 as uuidv7 } from 'uuid';

import { AiImageAssistantPanelContainer } from '@/components/ai-image-assistant';
import { CreateImageContent } from '@/components/asset-manager/components/create-image-content';
import type { Asset } from '@/components/asset-manager/types';
import { usePageSectionContext } from '@/components/page-sections/page-section-context';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';
import { TabsContent } from '@/components/ui/tabs';
import { useImageCreatorAssistant } from '@/hooks/mastra/use-image-creator-assistant';
import { useImageSuggestions } from '@/hooks/mastra/use-image-suggestions';
import { useMastraClient } from '@/hooks/mastra/use-mastra-client';
import { buildAiGeneratedAssetUploadParams, imageUploadSubject } from '@/lib/ai-asset-upload';
import http from '@/lib/http';
import { getMainSections } from '@/lib/page-utils';
import { urlUploadCmsAssetsPath } from '@/lib/routes';
import { cn, getDocument } from '@/lib/utils';
import { useAssetsStore } from '@/stores/assets-store';
import { usePageEditorStore } from '@/stores/page-editor-store';
import type { ImageSuggestionTarget, SelectedAssetData } from '@/types/image';
import type { SharedData } from '@/types/shared';
import { ASSISTANT_PANEL_RESPONSIVE_WIDTH_CLASS } from '../asset-manager/tabs/constants';
import { Heading3, Paragraph } from '../ui/typography';

function matchesImageUrl(asset: Asset, imageUrl: string) {
  return (
    asset.externalUrl === imageUrl ||
    asset.url === imageUrl ||
    asset.metadata?.sourceUrl === imageUrl
  );
}

interface ImageSelectorCreateImageFeatureProps {
  isOpen: boolean;
  isActive: boolean;
  initialImageUrl?: string | null;
  sectionId?: string;
  imageSuggestionTarget?: ImageSuggestionTarget;
  onRequestActivate: () => void;
  onSelectImage: (imageData: SelectedAssetData) => void;
  onClose: () => void;
  onBusyStateChange: (busy: boolean) => void;
}

export function ImageSelectorCreateImageFeature({
  isOpen,
  isActive,
  initialImageUrl,
  sectionId,
  imageSuggestionTarget,
  onRequestActivate,
  onSelectImage,
  onClose,
  onBusyStateChange,
}: ImageSelectorCreateImageFeatureProps) {
  const sectionContext = usePageSectionContext();
  const resolvedSectionId = sectionId ?? sectionContext?.sectionId;

  const pageId = usePageEditorStore((s) => s.page?.id ?? '');
  const assets = useAssetsStore((state) => state.assets);
  const assetsByFolder = useAssetsStore((state) => state.assetsByFolder);
  const loadAssets = useAssetsStore((state) => state.loadAssets);
  const singleAssets = useAssetsStore((state) => state.singleAssets);

  const persistedScopeId = usePageEditorStore((s) => {
    if (!resolvedSectionId) return undefined;
    const section = getMainSections(s.page).find((sec) => sec.id === resolvedSectionId);
    const value = section?.props?.aiImageScopeId;
    return typeof value === 'string' ? value : undefined;
  });
  const updateSection = usePageEditorStore((s) => s.updateSection);
  const sectionPropsForUpdateRef = useRef<Record<string, unknown> | undefined>(undefined);
  sectionPropsForUpdateRef.current = usePageEditorStore((s) => {
    if (!resolvedSectionId) return undefined;
    return getMainSections(s.page).find((sec) => sec.id === resolvedSectionId)?.props;
  });

  const generatedScopeIdRef = useRef<string | undefined>(undefined);
  if (!persistedScopeId && resolvedSectionId && !generatedScopeIdRef.current) {
    generatedScopeIdRef.current = uuidv7();
  }
  const generatedScopeId = generatedScopeIdRef.current;
  const sectionScopeId = persistedScopeId ?? generatedScopeId;

  const mastraClient = useMastraClient();
  const { suggestions: imageSuggestions, isLoading: isLoadingSuggestions } = useImageSuggestions({
    pageId,
    sectionId: resolvedSectionId,
    target: imageSuggestionTarget,
    enabled: isOpen && isActive,
    mastraClient,
  });

  const { currentUser, accountId } = usePage<SharedData>().props;
  const assistantScopeKey = useMemo(
    () => (sectionScopeId ? `image-selector-modal:${sectionScopeId}` : 'image-selector-modal'),
    [sectionScopeId],
  );
  const imageAssistant = useImageCreatorAssistant({
    userId: currentUser.id,
    accountId: accountId ?? '',
    scopeKey: assistantScopeKey,
    clearStoredThreadOnUnmount: !sectionScopeId,
  });

  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const [isInsertingImage, setIsInsertingImage] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<{
    imageUrl: string;
    prompt: string;
    rootPrompt?: string;
  } | null>(null);

  const lastGeneratedImageIdRef = useRef<string | undefined>(undefined);
  // True only while the modal should auto-hydrate the next generated image from assistant history.
  const shouldHydrateGeneratedImageRef = useRef(false);
  const pendingSubmitRef = useRef<{
    threadId: string;
    prompt: string;
    latestImageUrl?: string;
  } | null>(null);

  const lastImageItem = useMemo(() => {
    return [...imageAssistant.conversation].reverse().find((item) => item.type === 'image');
  }, [imageAssistant.conversation]);
  const isAwaitingGeneratedImage =
    shouldHydrateGeneratedImageRef.current && imageAssistant.isGenerating;
  const shouldShowGeneratingState =
    isAwaitingGeneratedImage && (!generatedImage || !isAssistantOpen);

  useEffect(() => {
    onBusyStateChange(isAssistantOpen);
  }, [isAssistantOpen, onBusyStateChange]);

  useEffect(() => {
    if (isOpen) return;
    shouldHydrateGeneratedImageRef.current = false;
    pendingSubmitRef.current = null;
  }, [isOpen]);

  useEffect(() => {
    const pending = pendingSubmitRef.current;
    if (!isActive || !pending) return;
    if (imageAssistant.threadId !== pending.threadId) return;

    pendingSubmitRef.current = null;

    void imageAssistant
      .sendMessage(
        pending.prompt,
        pending.latestImageUrl ? { latestImageUrl: pending.latestImageUrl } : undefined,
      )
      .catch(() => {
        toast.error('Failed to generate image. Please try again.');
      });
  }, [isActive, imageAssistant.threadId, imageAssistant.sendMessage]);

  useEffect(() => {
    if (!shouldHydrateGeneratedImageRef.current || !lastImageItem || lastImageItem.type !== 'image')
      return;
    if (lastImageItem.id === lastGeneratedImageIdRef.current) return;

    lastGeneratedImageIdRef.current = lastImageItem.id;
    shouldHydrateGeneratedImageRef.current = false;
    setGeneratedImage({
      imageUrl: lastImageItem.imageUrl,
      prompt: lastImageItem.prompt,
      rootPrompt: imageUploadSubject(lastImageItem),
    });
  }, [lastImageItem]);

  const clearAutoHydrateState = () => {
    shouldHydrateGeneratedImageRef.current = false;
  };

  const collectKnownAssets = (rootAssets: Asset[] = assets) => {
    const dedupedAssets = new Map<string, Asset>();

    for (const asset of rootAssets) {
      dedupedAssets.set(asset.id, asset);
    }
    for (const asset of Object.values(singleAssets)) {
      dedupedAssets.set(asset.id, asset);
    }
    for (const folderAssets of Object.values(assetsByFolder)) {
      for (const asset of folderAssets) {
        dedupedAssets.set(asset.id, asset);
      }
    }

    return [...dedupedAssets.values()];
  };

  const findMatchingAsset = (imageUrl: string, rootAssets: Asset[] = assets) => {
    return collectKnownAssets(rootAssets).find((asset) => matchesImageUrl(asset, imageUrl));
  };

  const handleCreateImageSubmit = async (prompt: string) => {
    if (pendingSubmitRef.current) return;

    // Persist scope id to section.props on the first generation only.
    if (resolvedSectionId && !persistedScopeId && generatedScopeId) {
      updateSection(
        resolvedSectionId,
        {
          props: {
            ...(sectionPropsForUpdateRef.current ?? {}),
            aiImageScopeId: generatedScopeId,
          },
        },
        { skipHistory: true },
      );
    }

    if (lastImageItem) {
      lastGeneratedImageIdRef.current = lastImageItem.id;
    }
    shouldHydrateGeneratedImageRef.current = true;
    setIsAssistantOpen(true);

    const latestImageUrl = lastImageItem?.imageUrl ?? initialImageUrl ?? undefined;
    const safeLatestImageUrl = /^https?:\/\//i.test(latestImageUrl ?? '')
      ? latestImageUrl
      : undefined;

    if (!imageAssistant.hasStoredThread) {
      const threadId = imageAssistant.createThread();
      pendingSubmitRef.current = {
        threadId,
        prompt,
        latestImageUrl: safeLatestImageUrl,
      };
      return;
    }

    try {
      await imageAssistant.sendMessage(
        prompt,
        safeLatestImageUrl ? { latestImageUrl: safeLatestImageUrl } : undefined,
      );
    } catch {
      toast.error('Failed to generate image. Please try again.');
    }
  };

  const handleInsertGeneratedImage = async () => {
    if (!generatedImage || isInsertingImage) return;
    setIsInsertingImage(true);
    try {
      let createdAsset = findMatchingAsset(generatedImage.imageUrl);

      if (!createdAsset) {
        const refreshedAssets = await loadAssets('images', true);
        createdAsset = findMatchingAsset(generatedImage.imageUrl, refreshedAssets);
      }

      createdAsset ??= (
        await http.post(
          urlUploadCmsAssetsPath(),
          buildAiGeneratedAssetUploadParams(
            generatedImage.imageUrl,
            imageUploadSubject(generatedImage),
          ),
        )
      ).data as Asset;
      onSelectImage({
        url: createdAsset.url ?? generatedImage.imageUrl,
        assetId: createdAsset.id,
        folderId: createdAsset.folderId,
        altText: createdAsset.altText ?? generatedImage.prompt,
        variants: createdAsset.variants,
      });
      clearAutoHydrateState();
      setIsAssistantOpen(false);
      setGeneratedImage(null);
      onClose();
    } catch {
      toast.error('Failed to save image. Please try again.');
    } finally {
      setIsInsertingImage(false);
    }
  };

  return (
    <>
      <TabsContent value="create" forceMount hidden={!isActive} className="min-h-0 overflow-y-auto">
        {generatedImage && !shouldShowGeneratingState ? (
          <div className="flex flex-col items-center gap-4 p-4">
            <Button
              variant="ghost"
              onClick={() => setIsAssistantOpen(true)}
              className="h-auto rounded-md p-0 hover:bg-transparent"
              aria-label="Open image in AI Assistant"
            >
              <img
                src={generatedImage.imageUrl}
                alt={generatedImage.prompt}
                className="max-h-[350px] w-auto rounded-md object-contain"
              />
            </Button>
          </div>
        ) : shouldShowGeneratingState ? (
          <div className="flex flex-col items-center justify-center gap-2 p-8">
            <Heading3>
              Generating Image <Icon icon={faSparkles} className="text-primary" />
            </Heading3>
            <Paragraph className="text-sm text-muted-foreground">
              Continuing in AI Assistant
            </Paragraph>
          </div>
        ) : (
          <CreateImageContent
            suggestions={imageSuggestions}
            isLoadingSuggestions={isLoadingSuggestions}
            onSubmit={handleCreateImageSubmit}
          />
        )}
      </TabsContent>

      {isActive && shouldShowGeneratingState ? (
        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              clearAutoHydrateState();
              imageAssistant.stop();
            }}
          >
            <Icon icon={faStop} className="text-destructive" />
            Cancel
          </Button>
        </DialogFooter>
      ) : isActive && generatedImage ? (
        <DialogFooter>
          <Button
            variant="outline"
            disabled={isInsertingImage}
            onClick={() => {
              clearAutoHydrateState();
              setGeneratedImage(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleInsertGeneratedImage()}
            isLoading={isInsertingImage}
            loadingText="Inserting..."
            loaderPlacement="start"
          >
            Insert Image
          </Button>
        </DialogFooter>
      ) : null}

      {(() => {
        if (!isAssistantOpen) return null;

        const body = getDocument()?.body;
        if (!body) return null;

        return createPortal(
          <div className="fixed inset-0 z-50 pointer-events-none">
            <div
              className={cn(
                'pointer-events-auto absolute border-border shadow-2xl',
                ASSISTANT_PANEL_RESPONSIVE_WIDTH_CLASS,
                'inset-y-0 right-0 border-l',
              )}
            >
              <AiImageAssistantPanelContainer
                className="size-full"
                assistant={imageAssistant}
                onGenerate={(prompt) => void handleCreateImageSubmit(prompt)}
                onClose={() => {
                  if (isInsertingImage) return;
                  setIsAssistantOpen(false);
                }}
                onImageClick={(item) => {
                  onRequestActivate();
                  setGeneratedImage({
                    imageUrl: item.imageUrl,
                    prompt: item.prompt,
                    rootPrompt: imageUploadSubject(item),
                  });
                }}
                threads={imageAssistant.threads}
                isLoadingThreads={imageAssistant.isLoadingThreads}
                threadId={imageAssistant.threadId}
                onCreateThread={() => {
                  imageAssistant.createThread();
                  setGeneratedImage(null);
                }}
              />
            </div>
          </div>,
          body,
        );
      })()}
    </>
  );
}
